import type { ContractType } from '@front/api/service-career/job-listing.types'
import type { JobListingItem } from '@front/api/service-career-neo/job-listing.types'
import {
  ExtraSmallText,
  TextBodyBold,
  type TypographyColor,
} from '@front/components/data-display/typography/Typography'
import { Stack, useTheme } from '@mui/material'
import CareerTag from '../career-tag/CareerTag'
import {
  DescriptionOutlined,
  MedicalServicesOutlined,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { ParseKeys } from 'i18next'
import { capitalizeFirst } from '@hublo/utils/string'
import { DateTime } from 'luxon'
import { Link } from '@tanstack/react-router'
import { JOB_OFFER_CONTEXT } from '@hublo/enums/recruitment/job-offer-context.enum'

type CardProps = {
  uuid?: string
  slug?: string
  contractTypes?: ContractType[]
  context?: JobListingItem['context']
  jobs?: string[]
  title?: string
  publishedAt?: string
}

export default function JobCard({
  uuid,
  slug,
  contractTypes,
  context,
  jobs,
  title,
  publishedAt,
}: CardProps) {
  const theme = useTheme()
  const { t } = useTranslation('main.worker')
  const { t: tJobs } = useTranslation('common.jobs')
  const contractIcon = (
    <DescriptionOutlined
      sx={{
        width: 16,
        height: 16,
        color: theme.palette.text.secondary,
      }}
    />
  )

  return (
    <>
      <Link
        to="/$slug/$uuid"
        params={{ slug: slug ?? '', uuid: uuid ?? '' }}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Stack
          sx={{
            boxShadow:
              '0 2px 4px 0 rgba(25, 23, 21, 0.03), 0 8px 8px 0 rgba(25, 23, 21, 0.02)',
            border: '1px solid',
            borderColor: theme.palette.border['brand-subtle'],
            borderRadius: '16px',
            padding: 2,
            backgroundColor: theme.palette.background.surface,
            gap: 1,
          }}
        >
          <TextBodyBold>{title}</TextBodyBold>
          <Stack
            sx={{
              gap: { md: 2, xs: 0 },
              flexDirection: { md: 'row', xs: 'column' },
              flexWrap: 'wrap',
            }}
          >
            {contractTypes?.[0]?.name && (
              <CareerTag label={contractTypes?.[0]?.name} icon={contractIcon} />
            )}

            {context === JOB_OFFER_CONTEXT.REPLACEMENT && (
              <CareerTag
                label={t('job_offers.filter.vacations')}
                icon={contractIcon}
              />
            )}

            {(jobs ?? []).map((j) => (
              <CareerTag
                key={j}
                label={tJobs(j.slice(12) as ParseKeys<'common.jobs'>)}
                icon={
                  <MedicalServicesOutlined sx={{ width: 16, height: 16 }} />
                }
              />
            ))}
          </Stack>
          {publishedAt && (
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <ExtraSmallText
                color={theme.palette.text.secondary as TypographyColor}
                data-testid="JobOffers__JobOfferCard__publishedAt"
              >
                {capitalizeFirst(
                  DateTime.fromISO(
                    publishedAt ?? '',
                  ).toRelativeCalendar() as string,
                )}
              </ExtraSmallText>
            </Stack>
          )}
        </Stack>
      </Link>
    </>
  )
}
